import { useCallback } from 'react'

import { Account } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useTaxRates } from '@modules-deprecated/app/taxrates'

import { BillFormFieldsChangeSource } from '../../../../../enums/billFormFieldsChangeSource'
import { useFormContext } from '../../../../../hooks/useForm'
import { useBillForm } from '../contexts/billFormContext'
import { autoCompleteBillForm } from '../utils/autoCompleteBillForm'
import { BillFormSchema } from '../utils/formData'
import { getBillFormFromExpenseCategory } from '../utils/getBillFormFromExpenseCategory'
import { useTrackAutocompletedFields } from '../utils/trackAutocompletedFields'

export const useExpenseCategoryAutoComplete = () => {
  const { organization } = useUserOrganization()
  const { getValues, reset: resetForm, setValue } = useFormContext<BillFormSchema>()
  const { taxRates } = useTaxRates()
  const { trackAutocompletedFields } = useTrackAutocompletedFields()
  const { billFormView, formFieldsChangeSource, setFormFieldsChangeSource } = useBillForm()

  const autoCompleteWithExpenseCategoryData = useCallback(
    (expenseCategory: Account | undefined, lineIndex: number, source: BillFormFieldsChangeSource) => {
      const billForm = getValues()
      const billFormExpense = getBillFormFromExpenseCategory({
        billLinesDefault: billForm.billLines,
        expenseCategory,
        lineIndex,
        linesTotal: billForm.billLines?.length,
        taxRates,
      })

      const billFormUpdated = autoCompleteBillForm({
        billFormDefault: billForm,
        billFormOverrides: billFormExpense,
        baseCurrencyId: organization?.baseCurrencyId,
      })
      resetForm(billFormUpdated, { keepDefaultValues: true })

      trackAutocompletedFields({
        billLines: billForm?.billLines?.[lineIndex],
        billFormView,
        context: source,
        formFieldsChangeSource,
        setFormFieldsChangeSource,
        updatedBillLines: billFormUpdated?.billLines?.[lineIndex],
      })

      // Wait one frame before setting autoCompletedFields
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', billFormUpdated.autoCompletedFields)
      })
    },
    [
      billFormView,
      formFieldsChangeSource,
      getValues,
      organization?.baseCurrencyId,
      resetForm,
      setFormFieldsChangeSource,
      setValue,
      taxRates,
      trackAutocompletedFields,
    ],
  )

  return { autoCompleteWithExpenseCategoryData }
}
